/**
 * Presentation rules shared by the ticket list and the ticket detail.
 *
 * Only mapping and arithmetic lives here — no JSX, no translation. The tone
 * maps are the single place that decides what "red" means for a ticket, so the
 * list badge and the detail header can never disagree.
 */

import type { BadgeVariant, StatusTone } from "@norican/react";

import { parseDateInput } from "@/components/common";

import type {
  TicketApprovalStatusCode,
  TicketBucket,
  TicketDetail,
  TicketKind,
  TicketOperation,
  TicketPriorityCode,
  TicketSortField,
  TicketStatusCode,
  TicketSummary,
} from "../api/contract";

/** The `tone` values the design system `Badge` accepts. */
export type BadgeTone = "neutral" | "accent" | "success" | "danger" | "warning" | "info";

/**
 * Status colour semantics for an incident — "something is broken".
 *
 * `WAITING_CUSTOMER` is warning rather than neutral on purpose: it is the one
 * status where nothing moves until the employee does something, and burying
 * that in grey is how tickets rot. The two "waiting on someone else" states are
 * deliberately grey for the opposite reason — nothing is expected of the
 * reader, so the eye should slide straight past them.
 */
const INCIDENT_STATUS_TONE: Record<TicketStatusCode, BadgeTone> = {
  LOGGED: "info",
  ACTIVE: "accent",
  WAITING_CUSTOMER: "warning",
  WAITING_THIRD_PARTY: "neutral",
  WAITING_RESOLUTION: "neutral",
  PENDING_APPROVAL: "warning",
  APPROVED: "success",
  APPROVAL_REJECTED: "danger",
  IN_PROCUREMENT: "info",
  RESOLVED: "success",
  FULFILLED: "success",
  CLOSED: "neutral",
  CANCELLED: "neutral",
  UNKNOWN: "neutral",
};

/**
 * Status colour semantics for a service request — "I need something".
 *
 * A second map rather than a shared one, because the two lifecycles mean
 * different things by the same code:
 *
 *   - `WAITING_THIRD_PARTY` is a stall on an incident and grey; on a request it
 *     is procurement doing its job, which is progress, and reads as `info`.
 *   - `WAITING_RESOLUTION` is likewise "being prepared" rather than "stuck".
 *   - `CANCELLED` is the mirror image: a cancelled incident is usually a
 *     duplicate, but a cancelled request means the thing you asked for is not
 *     coming — a fact the reader must not miss.
 *
 * The API models both lifecycles with the single `TicketStatusCode` enum, so
 * the split lives here in presentation. Do not collapse the two maps back into
 * one: they only look similar.
 */
const SERVICE_REQUEST_STATUS_TONE: Record<TicketStatusCode, BadgeTone> = {
  LOGGED: "info",
  ACTIVE: "accent",
  WAITING_CUSTOMER: "warning",
  WAITING_THIRD_PARTY: "info",
  WAITING_RESOLUTION: "info",
  PENDING_APPROVAL: "warning",
  APPROVED: "success",
  // Rejected is not a neutral ending. The requester has to be able to tell it
  // apart from a request that was simply completed.
  APPROVAL_REJECTED: "danger",
  // Somebody is buying something. That is progress, not a warning.
  IN_PROCUREMENT: "info",
  RESOLVED: "success",
  FULFILLED: "success",
  CLOSED: "neutral",
  CANCELLED: "danger",
  UNKNOWN: "neutral",
};

const STATUS_TONE: Record<TicketKind, Record<TicketStatusCode, BadgeTone>> = {
  INCIDENT: INCIDENT_STATUS_TONE,
  SERVICE_REQUEST: SERVICE_REQUEST_STATUS_TONE,
};

/**
 * The statuses where the ticket cannot move until the reader does something.
 * These are the only ones drawn `solid` — if everything shouts, nothing does.
 */
const ACTION_REQUIRED_STATUSES: readonly TicketStatusCode[] = [
  "WAITING_CUSTOMER",
  "PENDING_APPROVAL",
];

const PRIORITY_TONE: Record<TicketPriorityCode, BadgeTone> = {
  P1: "danger",
  P2: "warning",
  P3: "neutral",
  P4: "neutral",
  UNKNOWN: "neutral",
};

/**
 * Priority weight. Only `P1` is filled — a column of solid badges is a column
 * with no signal in it, and "critical" is the one value worth interrupting for.
 * `P4` is outlined so the quietest priority is also the quietest mark.
 */
const PRIORITY_VARIANT: Record<TicketPriorityCode, BadgeVariant> = {
  P1: "solid",
  P2: "subtle",
  P3: "subtle",
  P4: "outline",
  UNKNOWN: "outline",
};

/**
 * Icon colour per ticket kind, taken from the categorical chart ramp.
 *
 * Categorical and not semantic on purpose: which kind a ticket is says nothing
 * about whether it is going well, so it must not borrow the success/danger
 * palette that the status and SLA columns rely on. Both entries repoint per
 * theme, so they stay legible on the dark ground too.
 */
const KIND_ICON_CLASS: Record<TicketKind, string> = {
  INCIDENT: "text-chart-6",
  SERVICE_REQUEST: "text-chart-5",
};

const APPROVAL_TONE: Record<TicketApprovalStatusCode, BadgeTone> = {
  PENDING: "warning",
  APPROVED: "success",
  DENIED: "danger",
  TIMED_OUT: "neutral",
  UNKNOWN: "neutral",
};

/** The badge tone for a status, in the vocabulary of the ticket's own kind. */
export function statusTone(code: TicketStatusCode, kind: TicketKind): BadgeTone {
  return STATUS_TONE[kind]?.[code] ?? "neutral";
}

/**
 * How heavily a status badge is drawn: filled when the ticket is waiting on the
 * reader, outlined once it is over, subtle for everything in between.
 */
export function statusVariant(code: TicketStatusCode, isFinal: boolean): BadgeVariant {
  if (ACTION_REQUIRED_STATUSES.includes(code)) {
    return "solid";
  }
  return isFinal ? "outline" : "subtle";
}

export function priorityTone(code: TicketPriorityCode): BadgeTone {
  return PRIORITY_TONE[code] ?? "neutral";
}

export function priorityVariant(code: TicketPriorityCode): BadgeVariant {
  return PRIORITY_VARIANT[code] ?? "subtle";
}

/** Tailwind colour class for the icon that marks a ticket's kind. */
export function kindIconClass(kind: TicketKind): string {
  return KIND_ICON_CLASS[kind] ?? "text-fg-muted";
}

export function approvalTone(code: TicketApprovalStatusCode): BadgeTone {
  return APPROVAL_TONE[code] ?? "neutral";
}

/** Every bucket the list filter offers, in display order. */
export const TICKET_BUCKETS: readonly TicketBucket[] = [
  "OPEN",
  "AWAITING_ME",
  "RESOLVED",
  "CLOSED",
  "ALL",
];

/** Every status the multi-select filter offers, in lifecycle order. */
export const TICKET_STATUS_CODES: readonly TicketStatusCode[] = [
  "LOGGED",
  "ACTIVE",
  "WAITING_CUSTOMER",
  "WAITING_THIRD_PARTY",
  "WAITING_RESOLUTION",
  "PENDING_APPROVAL",
  "APPROVED",
  "RESOLVED",
  "FULFILLED",
  "CLOSED",
  "CANCELLED",
  "UNKNOWN",
];

export const TICKET_KINDS: readonly TicketKind[] = ["INCIDENT", "SERVICE_REQUEST"];

/** True when a raw string from `metadata` is a status code the portal knows. */
export function isTicketStatusCode(value: string): value is TicketStatusCode {
  return (TICKET_STATUS_CODES as readonly string[]).includes(value);
}

/**
 * Which catalogue a field's values are named in, for the audit timeline.
 *
 * Ivanti records a field change with its own vocabulary — `Logged`, `Medium`,
 * `P3` — while the ticket header beside it shows the portal's translated
 * labels. Two vocabularies on one screen, and in a French session the timeline
 * was the English one.
 *
 * `null` means the values are names, not codes: a person or a team is whatever
 * Ivanti calls them and translating it would be wrong.
 */
export const AUDIT_VALUE_CATALOGUE: Record<string, "status" | "level" | "priority" | null> = {
  status: "status",
  statusSet: "status",
  priority: "priority",
  prioritySet: "priority",
  impact: "level",
  impactSet: "level",
  urgency: "level",
  urgencySet: "level",
  team: null,
  teamSet: null,
  owner: null,
  ownerSet: null,
};

export const TICKET_LEVEL_CODES = ["LOW", "MEDIUM", "HIGH"] as const;

/**
 * Grid column id ⇄ contract sort field.
 *
 * `DataGrid` sorts by column id and the API sorts by an enum, so the mapping is
 * declared once and read in both directions.
 */
export const SORTABLE_COLUMNS: Readonly<Record<string, TicketSortField>> = {
  ticket_number: "TICKET_NUMBER",
  status: "STATUS",
  priority: "PRIORITY",
  created_at: "CREATED_AT",
  updated_at: "UPDATED_AT",
};

export function sortFieldForColumn(columnId: string): TicketSortField | null {
  return SORTABLE_COLUMNS[columnId] ?? null;
}

export function columnForSortField(field: TicketSortField): string {
  const match = Object.entries(SORTABLE_COLUMNS).find(([, value]) => value === field);
  return match?.[0] ?? "updated_at";
}

/** Whether the ticket's `permitted_operations` allow an operation. */
export function isPermitted(ticket: TicketDetail, operation: TicketOperation): boolean {
  return (ticket.permitted_operations ?? []).includes(operation);
}

// ─── SLA ─────────────────────────────────────────────────────────────────

/** How close to the target counts as "due soon". */
const DUE_SOON_MS = 24 * 60 * 60 * 1000;

export type SlaState = "BREACHED" | "DUE_SOON" | "ON_TRACK" | "NONE" | "DONE";

export interface SlaAssessment {
  state: SlaState;
  /** `StatusStrip` / cell tone for this state. */
  tone: StatusTone;
  /** Badge tone for the same state. */
  badgeTone: BadgeTone;
  /** The parsed target, or null when the ticket has none. */
  target: Date | null;
}

/**
 * `tone` drives the labelled `StatusStrip` cell on the detail page, where
 * "positive" is unambiguous because the cell says "Target" next to it.
 *
 * `badgeTone` drives the list column, and there `ON_TRACK` is deliberately
 * neutral rather than green: green already means "resolved" on the same row,
 * and a column where the common case is coloured is a column nobody scans. Only
 * the exceptions — due soon, past target — are allowed to take colour.
 */
const SLA_TONE: Record<SlaState, { tone: StatusTone; badgeTone: BadgeTone }> = {
  BREACHED: { tone: "negative", badgeTone: "danger" },
  DUE_SOON: { tone: "caution", badgeTone: "warning" },
  ON_TRACK: { tone: "positive", badgeTone: "neutral" },
  DONE: { tone: "neutral", badgeTone: "neutral" },
  NONE: { tone: "neutral", badgeTone: "neutral" },
};

/**
 * Decides how a ticket is doing against its SLA target.
 *
 * `sla_breached` from the server wins — the tenant's clock, not the browser's,
 * decides a breach. The local comparison only distinguishes "due soon" from
 * "on track" for a target the server has not yet flagged.
 */
export function assessSla(
  ticket: Pick<TicketSummary, "target_at" | "status"> & { sla_breached?: boolean },
  now: Date = new Date(),
): SlaAssessment {
  const target = parseDateInput(ticket.target_at);

  if (ticket.sla_breached === true) {
    return { state: "BREACHED", ...SLA_TONE.BREACHED, target };
  }
  if (ticket.status.is_final) {
    return { state: "DONE", ...SLA_TONE.DONE, target };
  }
  if (target === null) {
    return { state: "NONE", ...SLA_TONE.NONE, target: null };
  }

  const remaining = target.getTime() - now.getTime();
  if (remaining <= 0) {
    return { state: "BREACHED", ...SLA_TONE.BREACHED, target };
  }
  if (remaining <= DUE_SOON_MS) {
    return { state: "DUE_SOON", ...SLA_TONE.DUE_SOON, target };
  }
  return { state: "ON_TRACK", ...SLA_TONE.ON_TRACK, target };
}

/** Human file size for an attachment row, in the active locale. */
export function formatBytes(bytes: number, locale: string): string {
  const units = ["B", "kB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const formatted = new Intl.NumberFormat(locale, {
    maximumFractionDigits: unitIndex === 0 ? 0 : 1,
  }).format(value);
  return `${formatted} ${units[unitIndex] ?? "B"}`;
}

/**
 * The i18n key for a ticket's reference, in Ivanti's own wording.
 *
 * The service desk, the Ivanti console and the self-service portal all say
 * "Incident Id 366" and "Request Id 10343", so the employee-facing app says it
 * too — someone reading a number off this screen to a technician should be
 * reading the same words back.
 *
 * A key rather than a formatted string: German wants "Incident-ID", Chinese
 * puts the label first with a different separator, and building that here would
 * mean formatting translations in code.
 */
export type TicketReferenceKey =
  | "reference.id.INCIDENT"
  | "reference.id.SERVICE_REQUEST"
  | "reference.id.UNKNOWN"
  | "reference.heading.INCIDENT"
  | "reference.heading.SERVICE_REQUEST"
  | "reference.heading.UNKNOWN";

export function ticketReferenceKey(kind: string, form: "id" | "heading"): TicketReferenceKey {
  const known = kind === "INCIDENT" || kind === "SERVICE_REQUEST" ? kind : "UNKNOWN";
  return `reference.${form}.${known}` as TicketReferenceKey;
}
